/**
 * V267: 근무평가 공통 콘텐츠 컴포넌트
 *
 * WorkEvaluationTab(조직장)과 HrPerformancePage(인사팀) 양쪽에서 동일한 UI로 렌더링.
 * - 대상자 인적 사항
 * - 평가 사항 (rating 테이블)
 * - 의견 섹션 (text/select)
 * - 안내 문구
 */

import React from 'react';
import type {
  EvaluationItem,
  EvaluationItemV2,
  EvaluationNode,
  ScoringScale,
} from '../hooks/useEvaluations';

// ============================================
// 점수 등급 매핑 (5점 scale)
// ============================================

export const WORK_GRADE_LABELS: Record<number, string> = {
  5: '최우수',
  4: '우수',
  3: '양호',
  2: '부족',
  1: '불량',
};

// ============================================
// 스타일 상수
// ============================================

const infoLabelStyle: React.CSSProperties = {
  padding: '6px 10px', fontWeight: 600, fontSize: 12, color: '#6B7280',
  border: '1px solid #E5E7EB', width: '15%', background: '#F9FAFB',
};
const infoValueStyle: React.CSSProperties = {
  padding: '6px 10px', fontSize: 13, border: '1px solid #E5E7EB', width: '35%',
};
const thStyle: React.CSSProperties = {
  padding: '8px 10px', textAlign: 'center', fontWeight: 600, fontSize: 12,
};
const tdStyle: React.CSSProperties = {
  padding: '8px 10px', fontSize: 13,
};

// ============================================
// Props
// ============================================

export interface WorkEvaluationContentProps {
  evaluation: EvaluationItem;
  formData: Record<string, any>;
  isEditable?: boolean;
  onItemChange?: (itemId: string, value: any) => void;
  /** 계약 시작일/종료일 표시용 */
  targetEmployee?: {
    dispatchHireDate?: string;
    dispatchEndDate?: string;
    contractHireDate?: string;
    contractEndDate?: string;
  };
  /** V61: 상위 조직장 섹션을 부모에서 별도 렌더링할 때 중복 방지 */
  hideUpperEvaluator?: boolean;
}

// ============================================
// 메인 컴포넌트
// ============================================

export function WorkEvaluationContent({
  evaluation,
  formData,
  isEditable = false,
  onItemChange,
  targetEmployee,
  hideUpperEvaluator = false,
}: WorkEvaluationContentProps) {
  const isContract = evaluation.templateType === 'contract';
  const isDispatch = evaluation.templateType === 'dispatch';

  // 템플릿 데이터
  const nodes: EvaluationNode[] = (evaluation as any).templateNodes || evaluation.templateSnapshot?.nodes || [];
  const items: EvaluationItemV2[] = (evaluation as any).templateItems || evaluation.templateSnapshot?.items || [];
  const scale: ScoringScale = (evaluation as any).templateScoringScale || evaluation.templateSnapshot?.scoringScale || { min: 1, max: 5, step: 1 };

  // 평가 항목 (rating)
  const ratingItems = items.filter(it => it.inputType === 'rating' && it.isActive && it.requiredFor.includes('manager'));
  // 의견 항목 (text/select) — 종합의견을 해당부서 결정보다 위로
  const opinionItems = items
    .filter(it => (it.inputType === 'text' || it.inputType === 'select') && it.isActive && it.requiredFor.includes('manager'))
    .sort((a, b) => {
      const aIsOpinion = a.title.includes('종합의견') ? 0 : 1;
      const bIsOpinion = b.title.includes('종합의견') ? 0 : 1;
      return aIsOpinion - bIsOpinion || a.order - b.order;
    });

  // 가중점수 합계 계산
  const totalWeightedScore = ratingItems.reduce((sum, item) => {
    const score = Number(formData[item.id]) || 0;
    return sum + score * item.weight;
  }, 0);

  const handleItemChange = (itemId: string, value: any) => {
    onItemChange?.(itemId, value);
  };

  return (
    <>
      {/* 섹션 1: 대상자 인적 사항 */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8, color: '#111827' }}>1. 대상자 인적 사항</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <tbody>
            <tr style={{ background: '#F9FAFB' }}>
              <td style={infoLabelStyle}>부서명</td>
              <td style={infoValueStyle}>{evaluation.targetUserOrgUnitName || '-'}</td>
              <td style={infoLabelStyle}>성명</td>
              <td style={infoValueStyle}>{evaluation.targetUserName || '-'}</td>
            </tr>
            <tr>
              <td style={infoLabelStyle}>근로형태</td>
              <td style={infoValueStyle}>{isDispatch ? '파견직' : '계약직'}</td>
              <td style={infoLabelStyle}>평가 연도</td>
              <td style={infoValueStyle}>{evaluation.evaluationYear}년</td>
            </tr>
            {targetEmployee && (
              <tr style={{ background: '#F9FAFB' }}>
                <td style={infoLabelStyle}>계약 시작일</td>
                <td style={infoValueStyle}>
                  {(isDispatch ? targetEmployee.dispatchHireDate : targetEmployee.contractHireDate) || '-'}
                </td>
                <td style={infoLabelStyle}>계약 종료일</td>
                <td style={infoValueStyle}>
                  {(isDispatch ? targetEmployee.dispatchEndDate : targetEmployee.contractEndDate) || '-'}
                </td>
              </tr>
            )}
            {evaluation.evaluationPeriod && (
              <tr>
                <td style={infoLabelStyle}>평가 기간</td>
                <td style={infoValueStyle} colSpan={3}>{evaluation.evaluationPeriod}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 섹션 2: 평가 사항 */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8, color: '#111827' }}>2. 대상자 평가 사항</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#F3F4F6', borderBottom: '2px solid #D1D5DB' }}>
              <th style={thStyle}>#</th>
              <th style={{ ...thStyle, textAlign: 'left' }}>평가 항목</th>
              <th style={thStyle}>평가등급</th>
              {isDispatch && <th style={thStyle}>가중치</th>}
              <th style={thStyle}>{isDispatch ? '가중점수' : '점수'}</th>
              <th style={{ ...thStyle, textAlign: 'left', minWidth: 180 }}>평가 내용</th>
            </tr>
          </thead>
          <tbody>
            {ratingItems.map((item, idx) => {
              const score = Number(formData[item.id]) || 0;
              const weightedScore = score * item.weight;
              return (
                <tr key={item.id} style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <td style={{ ...tdStyle, textAlign: 'center', width: 30 }}>{idx + 1}</td>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>{item.title}</td>
                  <td style={{ ...tdStyle, textAlign: 'center', width: 120 }}>
                    {isEditable ? (
                      <select
                        value={formData[item.id] || ''}
                        onChange={e => handleItemChange(item.id, Number(e.target.value))}
                        style={{ padding: '4px 6px', border: '1px solid #D1D5DB', borderRadius: 4, fontSize: 12, width: '100%' }}
                      >
                        <option value="">선택</option>
                        {[5, 4, 3, 2, 1].map(v => (
                          <option key={v} value={v}>{WORK_GRADE_LABELS[v]}</option>
                        ))}
                      </select>
                    ) : (
                      <span>{WORK_GRADE_LABELS[score] || '-'}</span>
                    )}
                  </td>
                  {isDispatch && (
                    <td style={{ ...tdStyle, textAlign: 'center', width: 60, fontSize: 12 }}>{item.weight}</td>
                  )}
                  <td style={{ ...tdStyle, textAlign: 'center', width: 70, fontWeight: 600 }}>
                    {score ? (isDispatch ? weightedScore.toFixed(2) : score) : '-'}
                  </td>
                  <td style={{ ...tdStyle, fontSize: 12, color: '#6B7280' }}>{item.description || ''}</td>
                </tr>
              );
            })}
            {/* 합계 행 */}
            <tr style={{ background: '#F0FDF4', borderTop: '2px solid #10B981', fontWeight: 700 }}>
              <td colSpan={isDispatch ? 3 : 2} style={{ ...tdStyle, textAlign: 'right' }}>계</td>
              {isDispatch && <td style={{ ...tdStyle, textAlign: 'center' }}>1</td>}
              <td style={{ ...tdStyle, textAlign: 'center' }}>{totalWeightedScore.toFixed(2)}</td>
              <td style={tdStyle}></td>
            </tr>
          </tbody>
        </table>

        {/* 총 평가점수 */}
        <div style={{
          marginTop: 8, padding: '8px 16px', background: '#F0FDF4', borderRadius: 8,
          display: 'flex', alignItems: 'center', gap: 8, fontSize: 14,
        }}>
          <span style={{ fontWeight: 600 }}>근무 평점에 대한 결과</span>
          <span>총 평가점수(가중점수)</span>
          <span style={{ fontWeight: 700, fontSize: 18, color: '#059669' }}>
            {totalWeightedScore.toFixed(2)}
          </span>
          <span>점</span>
          {isDispatch && totalWeightedScore > 0 && totalWeightedScore < 3 && (
            <span style={{ color: '#DC2626', fontWeight: 600, marginLeft: 8 }}>
              ⚠ 3점 미만 — 파견계약종료 대상
            </span>
          )}
          {isContract && totalWeightedScore > 0 && totalWeightedScore <= 2 && (
            <span style={{ color: '#DC2626', fontWeight: 600, marginLeft: 8 }}>
              ⚠ 2점 이하 — 계약연장/종료 검토 대상
            </span>
          )}
        </div>
      </div>

      {/* 의견 섹션 */}
      <div style={{ marginBottom: 24 }}>
        {opinionItems.map(item => (
          <div key={item.id} style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
              {item.title}
            </label>
            {item.inputType === 'select' ? (
              isEditable ? (
                <select
                  value={formData[item.id] || ''}
                  onChange={e => handleItemChange(item.id, e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13 }}
                >
                  <option value="">선택하세요</option>
                  {(item.options || []).map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : (
                <div style={{ padding: '8px 12px', background: '#F9FAFB', borderRadius: 6, fontSize: 13 }}>
                  {formData[item.id] || '-'}
                </div>
              )
            ) : (
              isEditable ? (
                <textarea
                  value={formData[item.id] || ''}
                  onChange={e => handleItemChange(item.id, e.target.value)}
                  rows={3}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, resize: 'vertical' }}
                  placeholder={`${item.title}을(를) 입력하세요`}
                />
              ) : (
                <div style={{ padding: '8px 12px', background: '#F9FAFB', borderRadius: 6, fontSize: 13, minHeight: 40, whiteSpace: 'pre-wrap' }}>
                  {formData[item.id] || '-'}
                </div>
              )
            )}
          </div>
        ))}
      </div>

      {/* 상위 조직장 표시 (readonly) — hideUpperEvaluator=true일 때 부모에서 별도 렌더링 */}
      {!hideUpperEvaluator && (evaluation.upperEvaluatorName || evaluation.upperEvaluatorId) && (
        <div style={{
          marginBottom: 16, padding: '12px 16px', borderRadius: 8,
          border: '1px solid #D1D5DB', background: '#F9FAFB',
        }}>
          <div style={{ fontSize: 13 }}>
            <span style={{ fontWeight: 600 }}>제출 대상(상위 조직장): </span>
            <span style={{ color: '#111827' }}>{evaluation.upperEvaluatorName || evaluation.upperEvaluatorId}</span>
          </div>
        </div>
      )}

      {/* 안내 문구 */}
      <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.6, marginBottom: 16 }}>
        <p>* 평균을 양호로 하며, 우수하다고 여겨질 경우 우수로 하고 아주 우수하다고 판단 할 경우 최우수로 합니다.</p>
        <p>* 평가등급을 '최우수/우수/양호/부족/불량' 중에 해당되는 등급을 기입하면 자동으로 점수가 계산됩니다.</p>
        {isDispatch && <p>* 평가점수 3점 미만인 경우 파견계약종료 대상입니다.</p>}
        {isContract && <p>* 평가점수 2점 이하인 경우 계약연장 또는 계약종료 대상입니다.</p>}
        {isContract && <p>* 1차 평가자는 조직장, 2차 평가자는 실장으로 지정합니다. (1차 가중치 60%, 2차 가중치 40%)</p>}
        <p>* 인사팀으로 최종 근무 평가서를 제출해 주시면, 평가서를 바탕으로 검토한 뒤 전환여부에 대해 안내 드릴 예정입니다.</p>
      </div>
    </>
  );
}
